import * as THREE from 'three';
import { VEHICLES } from '@/entities/vehicles';
import { ModelLoader, modelLoader } from '@/render/ModelLoader';
import { applyCarMaterials } from '@/entities/CarView';
import { BallView } from '@/entities/BallView';
import { EnvironmentBuilder } from '@/render/Environment';
import { subscribeViewport, type ViewportUnsubscribe } from '@/ui/viewport';
import { tuning } from '@/config/tuning';
import {
  catchInNet,
  netDepthAt,
  netGripKeep,
  roofHold,
  type GoalCfg,
  type GoalFrame,
} from '@/physics/goalNet';
import { buildGoal, type GoalNet, type GoalNetBody } from '@/world/scenery/finishGoal';

/**
 * IntroClip.ts — the HOW TO PLAY opening "gameplay clip" (M86b, restaged
 * M86c). A short letterboxed cinematic rendered with the REAL game assets:
 * the player's red sports car dribbling the actual race BallView, with a
 * blue RIVAL pair chasing in the second lane, into the FINISH GOAL (M157 —
 * the same goal the races end in, built by scenery/finishGoal.ts at this
 * scene's smaller scale, with the same net physics).
 *
 * Three broadcast shots (owner spec, M86c; restaged for the goal, M157):
 *   1. CHASE — the camera rides behind the pack, the goal waiting ahead
 *      across the whole road.
 *   2. GOAL-LINE CAMERA — a hard cut to a low camera beside the post with a
 *      quick zoom-in and SLOW MOTION as the winning ball crosses the white
 *      goal line and bends into the corner of the net.
 *   3. REVERSE FROM BEHIND THE GOAL — speed snaps back: the net bulges
 *      toward the camera, the hero car is caught in it, the rival scores
 *      second, then the overlay fades into chapter 1.
 *
 * One promise per play(): resolves when the clip ends OR the player taps to
 * skip. Reduced motion never plays it (the caller skips straight to the
 * guide). The renderer is created on first play and kept (LobbyStage pattern).
 */

// ---- Scene layout (world units; the cars drive +X) -------------------------
const FINISH_X = 6; // where the goal line crosses the road
const ROAD_HALF_W = 11; // guardrail-to-guardrail half width
const START_X = -78; // where the run begins (deep in the fog)
const END_X = 30; // where the hero coasts to past the line
const RUN_S = 3.4; // WORLD-clock seconds for the hero run (slo-mo stretches real time)
const BALL_LEAD = 3.2; // a ball rides this far off its car's nose
const BALL_R = 1.25; // ball radius at clip scale
const LANE_Z = -3.2; // hero lane
const RIVAL_Z = 3.4; // rival lane (other side of the centre line)
const RIVAL_GAP = 14; // how far the rival trails the hero

// ---- The goal (M157) ---------------------------------------------------------
/** The race goal scaled to this scene's narrower road (22 wide vs 36). */
const GOAL_K = ROAD_HALF_W / tuning.track.halfWidth;
const CLIP_GOAL: GoalCfg = {
  ...tuning.goal,
  height: tuning.goal.height * GOAL_K,
  depth: tuning.goal.depth * GOAL_K,
  depthTop: tuning.goal.depthTop * GOAL_K,
  postRadius: tuning.goal.postRadius * GOAL_K,
  maxStretch: tuning.goal.maxStretch * GOAL_K,
};
/** The guardrail here is a 0.5-thick box centred 0.4 outside the road, so
 *  the goal's mouth runs to the rail's inner face and the posts stand in it. */
const GOAL_HALF_W = ROAD_HALF_W + 0.15;
const RAIL_TOP = 1.43;
const BALL_G = 30; // the race ball's gravity
/** How each scorer's ball leaves the line: a pop + a bend into a corner. */
const HERO_SHOT = { pop: 7.5, bend: -15 };
const RIVAL_SHOT = { pop: 4, bend: 9 };

// ---- The photo-finish beat --------------------------------------------------
const SLOMO_ZONE = 9; // hero ball this close to the line cuts to the line cam
const SLOMO_SCALE = 0.2; // slow-motion time scale
const SLOMO_TAIL_S = 0.5; // real seconds of slo-mo kept AFTER the cross
const HOLD_S = 1.6; // real seconds on the reverse wide before the fade
const FADE_S = 0.8; // overlay fade into chapter 1
const FAILSAFE_S = 12; // wall-clock kill switch, whatever happens

interface Confetto {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  spin: THREE.Vector3;
}

type Shot = 'chase' | 'photo' | 'wide';

/** The road surface: asphalt + centre dashes, painted once on a canvas. */
function roadTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 1024;
  c.height = 256;
  const g = c.getContext('2d')!;
  // Asphalt with a little tonal noise so it doesn't read flat.
  g.fillStyle = '#20242e';
  g.fillRect(0, 0, 1024, 256);
  for (let i = 0; i < 900; i++) {
    const v = 26 + Math.floor(Math.random() * 22);
    g.fillStyle = `rgba(${v},${v + 3},${v + 9},0.5)`;
    g.fillRect(Math.random() * 1024, Math.random() * 256, 2, 2);
  }
  // Lane dashes (u = along the road).
  g.fillStyle = 'rgba(232,237,245,0.75)';
  for (let x = 0; x < 1024; x += 64) g.fillRect(x, 124, 30, 8);
  // Edge lines.
  g.fillStyle = 'rgba(255,211,74,0.5)';
  g.fillRect(0, 18, 1024, 5);
  g.fillRect(0, 233, 1024, 5);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** One racer pair: a car group + its colour-matched ball. */
interface Pair {
  car: THREE.Group;
  ball: BallView;
  /** Half the car's length: what meets the net. */
  reach: number;
  /** Once its ball crosses the line, the pair runs free: the ball flies
   *  into the net and the car coasts in after it, both caught by the net
   *  physics the races use (physics/goalNet.ts). */
  free: Free | null;
}

interface Free {
  bx: number;
  by: number;
  bz: number;
  bvx: number;
  bvy: number;
  bvz: number;
  cx: number;
  cz: number;
  cv: number;
}

export class IntroClip {
  private readonly root: HTMLDivElement;
  private readonly caption: HTMLDivElement;
  private readonly skip: HTMLButtonElement;
  private renderer: THREE.WebGLRenderer | null = null;
  private env: EnvironmentBuilder | null = null;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(44, 16 / 9, 0.1, 400);
  private hero: Pair | null = null;
  private rival: Pair | null = null;
  private flash: THREE.Mesh | null = null;
  private finishMat: THREE.MeshBasicMaterial | null = null;
  private net: GoalNet | null = null;
  private readonly bodies: GoalNetBody[] = [];
  private confetti: Confetto[] = [];
  private raf: number | null = null;
  private lastT = 0;
  private worldT = 0; // sim clock (slo-mo scales this)
  private wallT = 0; // real clock (fade/hold/failsafe run on this)
  private sinceCross = -1; // real seconds since the winning cross (-1 = not yet)
  private timeScale = 1;
  private shot: Shot = 'chase';
  private fov = 44;
  private carsReady: Promise<unknown> = Promise.resolve();
  private resolvePlay: (() => void) | null = null;
  private unsubViewport: ViewportUnsubscribe | null = null;
  private readonly onResize = (): void => this.resize();

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'howto-clip';
    this.root.style.display = 'none';
    parent.appendChild(this.root);

    // Letterbox bars + caption + skip chip layer over the canvas.
    const barTop = document.createElement('div');
    barTop.className = 'howto-clip-bar top';
    const barBot = document.createElement('div');
    barBot.className = 'howto-clip-bar bottom';
    this.caption = document.createElement('div');
    this.caption.className = 'howto-clip-caption';
    this.caption.textContent = 'FIRST BALL INTO THE GOAL WINS';
    this.skip = document.createElement('button');
    this.skip.type = 'button';
    this.skip.className = 'howto-clip-skip';
    this.skip.textContent = 'TAP TO SKIP ›';
    this.root.append(barTop, barBot, this.caption, this.skip);
    // The whole clip is tappable (the chip is just the affordance).
    this.root.addEventListener('click', () => this.finish());
    this.unsubViewport = subscribeViewport(this.onResize);
  }

  /** Play the clip; resolves when it ends or is skipped. */
  play(): Promise<void> {
    this.ensureScene();
    this.root.style.display = '';
    this.root.classList.remove('out');
    this.caption.classList.remove('on');
    this.resetActors();
    this.resize();
    // Paint the empty night road immediately so the overlay is never blank.
    this.renderer?.render(this.scene, this.camera);
    return new Promise((resolve) => {
      this.resolvePlay = resolve;
      const begin = (): void => {
        if (!this.resolvePlay || this.raf !== null) return; // skipped while waiting
        this.resetActors();
        this.lastT = performance.now();
        const tick = (): void => {
          this.raf = requestAnimationFrame(tick);
          const now = performance.now();
          const dt = Math.min(0.1, (now - this.lastT) / 1000);
          this.lastT = now;
          this.step(dt);
        };
        this.raf = requestAnimationFrame(tick);
      };
      // The FIRST open races the GLB fetches — never start the run before the
      // cars exist (measured: a lone rolling ball). Cap the wait so a slow
      // network can only delay the clip, not stall the guide.
      void Promise.race([this.carsReady, new Promise((r) => setTimeout(r, 1500))]).then(begin);
    });
  }

  /** One step (public-ish for ?debug=1 manual stepping). `dt` is REAL time;
   *  world physics run on `dt * timeScale` (the slo-mo). */
  step(dt: number): void {
    this.wallT += dt;

    // Where is the hero ball, before this step? Drives the shot changes.
    const preBallX = this.hero ? this.hero.ball.mesh.position.x : START_X;

    // Slow motion holds through the crossing beat, then snaps back.
    const wantSlomo =
      (this.sinceCross < 0 && preBallX > FINISH_X - SLOMO_ZONE) ||
      (this.sinceCross >= 0 && this.sinceCross < SLOMO_TAIL_S);
    const target = wantSlomo ? SLOMO_SCALE : 1;
    this.timeScale += (target - this.timeScale) * Math.min(1, dt * 9);
    const wdt = dt * this.timeScale;
    this.worldT += wdt;
    if (this.sinceCross >= 0) this.sinceCross += dt;

    // ---- The run (world clock): hero sweeps in hot, rival chases ----------
    const runT = Math.min(1, this.worldT / RUN_S);
    const eased = 1 - Math.pow(1 - runT, 1.65);
    const carX = START_X + (END_X - START_X) * eased;
    const speed = ((END_X - START_X) * 1.65 * Math.pow(1 - runT, 0.65)) / RUN_S;
    this.drive(this.hero, carX, LANE_Z, speed, wdt, 0, HERO_SHOT);
    this.drive(this.rival, carX - RIVAL_GAP, RIVAL_Z, speed, wdt, 2.1, RIVAL_SHOT);
    this.pressNet();

    const ballX = this.hero ? this.hero.ball.mesh.position.x : carX + BALL_LEAD;
    if (this.sinceCross < 0 && ballX >= FINISH_X) this.cross();

    // ---- The three shots ----------------------------------------------------
    let shot: Shot = 'chase';
    if (this.sinceCross >= 0.9) shot = 'wide';
    else if (wantSlomo || this.sinceCross >= 0) shot = 'photo';
    if (shot !== this.shot) {
      this.shot = shot;
      // Hard cuts, broadcast style: entering the line cam starts wider and
      // ZOOMS IN through the slo-mo (the owner's photo-finish push).
      this.fov = shot === 'photo' ? 40 : shot === 'wide' ? this.fitFov(46, 62) : 44;
    }
    if (shot === 'chase') {
      // Riding high behind the pack: hero + ball ahead, rival chasing below,
      // the checkered band crossing the road up ahead. High + far, or the
      // rival ball fills the windshield (measured).
      this.camera.position.set(carX - 25, 8.8, LANE_Z + 13.5);
      this.camera.lookAt(carX + 8, 0.6, LANE_Z);
      this.easeFov(42, dt);
    } else if (shot === 'photo') {
      // The goal-line camera: low beside the far post, just in front of the
      // line, tight on the ball as it crosses and bends into the net.
      // Pulled back enough that the post, crossbar and net frame the ball
      // (zoomed to 26 the ball filled the shot and hid the goal — measured).
      const b = this.hero?.ball.mesh.position;
      this.camera.position.set(FINISH_X - 8.5, 3.0, 9.5);
      this.camera.lookAt(b ? b.x : ballX, b ? Math.max(BALL_R, b.y * 0.8) : BALL_R, b ? b.z : LANE_Z);
      this.easeFov(32, dt);
    } else {
      // Reverse from high behind the goal, looking down over the board into
      // the net: both balls bulging it toward us, the caught cars, the rival
      // scoring second. Low, the board walled off the bottom of the shot.
      this.camera.position.set(FINISH_X + 21, 8, 2.5);
      this.camera.lookAt(FINISH_X + 2, 1.2, -0.5);
      this.easeFov(this.fitFov(46, 62), dt);
    }

    // Flash decay (real time) + confetti physics (world time — they drift
    // dreamily through the slo-mo, then fall normally).
    if (this.flash && (this.flash.material as THREE.MeshBasicMaterial).opacity > 0) {
      const m = this.flash.material as THREE.MeshBasicMaterial;
      m.opacity = Math.max(0, m.opacity - dt * 1.3);
    }
    for (const c of this.confetti) {
      c.vel.y -= 22 * wdt;
      c.mesh.position.addScaledVector(c.vel, wdt);
      c.mesh.rotation.x += c.spin.x * wdt;
      c.mesh.rotation.y += c.spin.y * wdt;
      c.mesh.rotation.z += c.spin.z * wdt;
      if (c.mesh.position.y < 0.05) c.mesh.visible = false;
    }

    // The hand-off: hold the wide, fade, done. Wall-clock failsafe regardless.
    if (this.sinceCross >= HOLD_S + 0.9) this.root.classList.add('out');
    this.renderer?.render(this.scene, this.camera);
    if (this.sinceCross >= HOLD_S + 0.9 + FADE_S || this.wallT >= FAILSAFE_S) this.finish();
  }

  /** Drive one car+ball pair along its lane. */
  private placePair(
    pair: Pair | null,
    x: number,
    z: number,
    speed: number,
    wdt: number,
    phase: number,
  ): void {
    if (!pair) return;
    const wob = Math.sin(this.worldT * 7 + phase) * 0.12;
    pair.car.position.set(x, 0, z + wob);
    pair.car.rotation.y = Math.PI / 2 + wob * 0.08;
    const bx = x + BALL_LEAD + Math.sin(this.worldT * 5.1 + phase) * 0.35;
    pair.ball.mesh.position.set(bx, BALL_R, z + wob * 1.4);
    pair.ball.mesh.rotateOnWorldAxis(new THREE.Vector3(0, 0, -1), (speed * wdt) / BALL_R);
  }

  /** One pair this step: dribbling down its lane until its ball crosses the
   *  line, then running free into the net. */
  private drive(
    pair: Pair | null,
    x: number,
    z: number,
    speed: number,
    wdt: number,
    phase: number,
    shot: { pop: number; bend: number },
  ): void {
    if (!pair) return;
    if (!pair.free) {
      this.placePair(pair, x, z, speed, wdt, phase);
      const b = pair.ball.mesh.position;
      if (b.x < FINISH_X) return;
      pair.free = {
        bx: b.x,
        by: b.y,
        bz: b.z,
        bvx: speed * 1.08,
        bvy: shot.pop,
        bvz: shot.bend,
        cx: pair.car.position.x,
        cz: pair.car.position.z,
        cv: speed,
      };
    }
    this.runFree(pair, pair.free, wdt);
  }

  /** The free run: the race net physics on the ball and the car. */
  private runFree(pair: Pair, fr: Free, wdt: number): void {
    const cfg = CLIP_GOAL;
    // the ball: flight, turf bounce, the side nets (the rails), roof + net
    fr.bvy -= BALL_G * wdt;
    fr.bx += fr.bvx * wdt;
    fr.by += fr.bvy * wdt;
    fr.bz += fr.bvz * wdt;
    if (fr.by < BALL_R) {
      fr.by = BALL_R;
      if (fr.bvy < 0) fr.bvy = -fr.bvy * 0.42 < 1 ? 0 : -fr.bvy * 0.42;
      const roll = Math.max(0, 1 - 1.4 * wdt);
      fr.bvx *= roll;
      fr.bvz *= roll;
    }
    const side = ROAD_HALF_W - BALL_R;
    if (Math.abs(fr.bz) > side) {
      fr.bz = Math.sign(fr.bz) * side;
      fr.bvz *= -0.15;
    }
    const roof = roofHold(fr.bx - FINISH_X, fr.by, BALL_R, fr.bvy, cfg);
    if (roof) {
      fr.by = roof.h;
      fr.bvy = roof.vh;
    }
    const net = catchInNet(fr.bx - FINISH_X, BALL_R, netDepthAt(fr.by, cfg), fr.bvx, wdt, cfg);
    if (net) {
      fr.bx = FINISH_X + net.s;
      fr.bvx = net.vAlong;
      const keep = netGripKeep(wdt, cfg);
      fr.bvz *= keep;
      if (fr.bvy > 0) fr.bvy *= keep;
    }
    const v = Math.hypot(fr.bvx, fr.bvz);
    if (v > 1e-3) {
      const axis = new THREE.Vector3(fr.bvz / v, 0, -fr.bvx / v);
      pair.ball.mesh.rotateOnWorldAxis(axis, (v * wdt) / BALL_R);
    }
    pair.ball.mesh.position.set(fr.bx, fr.by, fr.bz);
    // the car: coasts in after it and is caught too
    fr.cv *= Math.max(0, 1 - 0.7 * wdt);
    fr.cx += fr.cv * wdt;
    const cn = catchInNet(fr.cx - FINISH_X, pair.reach, netDepthAt(0.7, cfg), fr.cv, wdt, cfg);
    if (cn) {
      fr.cx = FINISH_X + cn.s;
      fr.cv = cn.vAlong;
    }
    pair.car.position.set(fr.cx, 0, fr.cz);
    pair.car.rotation.y = Math.PI / 2;
  }

  /** Press both pairs into the goal's net (it bulges + ripples on its own
   *  clock, so the ripple slows with the slow motion too). */
  private pressNet(): void {
    if (!this.net) return;
    this.bodies.length = 0;
    for (const pair of [this.hero, this.rival]) {
      if (!pair) continue;
      const b = pair.ball.mesh.position;
      const fr = pair.free;
      this.bodies.push({
        key: pair.ball,
        x: b.x,
        y: b.y,
        z: b.z,
        reach: BALL_R,
        up: BALL_R,
        coreLat: BALL_R * 0.75,
        coreH: BALL_R * 0.75,
        vInto: fr ? fr.bvx : 0,
      });
      const c = pair.car.position;
      this.bodies.push({
        key: pair.car,
        x: c.x,
        y: 0.7,
        z: c.z,
        reach: pair.reach,
        up: 0.7,
        coreLat: 1.0,
        coreH: 0.7,
        vInto: fr ? fr.cv : 0,
      });
    }
    this.net.press(this.bodies, this.worldT, false);
  }

  /** A vertical fov of at least `v` that also shows at least `h` degrees
   *  across — a tall (portrait) screen would crop the corners of the goal. */
  private fitFov(v: number, h: number): number {
    const across = 2 * Math.atan(Math.tan((h * Math.PI) / 360) / Math.max(0.1, this.camera.aspect));
    return Math.max(v, (across * 180) / Math.PI);
  }

  private easeFov(target: number, dt: number): void {
    this.fov += (target - this.fov) * Math.min(1, dt * 5);
    this.camera.fov = this.fov;
    this.camera.updateProjectionMatrix();
  }

  /** The moment the winning ball crosses the line. */
  private cross(): void {
    this.sinceCross = 0;
    this.caption.classList.add('on');
    if (this.flash) {
      // Billboard the flash at the camera so it reads as a light burst from
      // any angle (a fixed plane is invisible edge-on).
      this.flash.quaternion.copy(this.camera.quaternion);
      (this.flash.material as THREE.MeshBasicMaterial).opacity = 0.85;
    }
    if (this.finishMat) this.finishMat.color.set(0xffe9a3);
    for (const c of this.confetti) {
      c.mesh.visible = true;
      c.mesh.position.set(
        FINISH_X + (Math.random() - 0.5) * 3,
        0.4,
        LANE_Z + (Math.random() - 0.5) * 8,
      );
      c.vel.set((Math.random() - 0.5) * 7, 9 + Math.random() * 8, (Math.random() - 0.5) * 7);
    }
  }

  /** Cancel a playing clip from the outside (the onboarding flow-SKIP jumps
   *  faces while the clip may still be rolling). Safe no-op when idle.
   *  NOTE: named cancel, not skip — `skip` is the TAP TO SKIP chip field. */
  cancel(): void {
    this.finish();
  }

  private finish(): void {
    if (!this.resolvePlay) return;
    const resolve = this.resolvePlay;
    this.resolvePlay = null;
    if (this.raf !== null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    this.root.style.display = 'none';
    resolve();
  }

  // ---- Scene construction ---------------------------------------------------

  private ensureScene(): void {
    if (this.renderer) return;
    const r = new THREE.WebGLRenderer({ antialias: true });
    r.setClearColor(0x05070f, 1);
    r.toneMapping = THREE.ACESFilmicToneMapping;
    r.outputColorSpace = THREE.SRGBColorSpace;
    r.domElement.className = 'howto-clip-canvas';
    this.root.prepend(r.domElement);
    this.renderer = r;

    this.scene.fog = new THREE.Fog(0x05070f, 40, 150);
    this.env = new EnvironmentBuilder(r);
    this.scene.environment = this.env.generate({
      skyTop: 0x0a1226,
      skyHorizon: 0x2a3450,
      sun: 0xfff2dc,
      sunIntensity: 1.8,
      sunDir: new THREE.Vector3(-0.3, 0.8, 0.5).normalize(),
      ground: 0x06080e,
    });

    this.scene.add(new THREE.HemisphereLight(0x9fb4dd, 0x0b0e16, 1.05));
    const key = new THREE.DirectionalLight(0xfff2dc, 2.2);
    key.position.set(-8, 14, 10);
    this.scene.add(key);
    // The finish-line floodlight pool: the hero moment is the bright spot.
    const flood = new THREE.PointLight(0xffe9b0, 320, 70, 1.9);
    flood.position.set(FINISH_X, 12, 0);
    this.scene.add(flood);

    // Road ribbon + shoulders.
    const roadTex = roadTexture();
    roadTex.repeat.set(16, 1);
    const road = new THREE.Mesh(
      new THREE.PlaneGeometry(260, ROAD_HALF_W * 2),
      new THREE.MeshStandardMaterial({ map: roadTex, roughness: 0.92 }),
    );
    road.rotation.x = -Math.PI / 2;
    this.scene.add(road);
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(500, 300),
      new THREE.MeshStandardMaterial({ color: 0x0a0d16, roughness: 1 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.05;
    this.scene.add(ground);

    // THE GOAL (M157): the race goal, scaled to this road, standing on the
    // white goal line — the same posts, net and board the races end in.
    const frame: GoalFrame = {
      x: FINISH_X,
      y: 0,
      z: 0,
      fx: 1,
      fz: 0,
      rx: 0,
      rz: 1,
      halfWidth: GOAL_HALF_W,
    };
    const goal = buildGoal({
      frame,
      cfg: CLIP_GOAL,
      halfWidth: GOAL_HALF_W,
      turf: () => 0,
      edge: (_s, side) => side * (GOAL_HALF_W + CLIP_GOAL.postRadius),
      railTop: RAIL_TOP,
    });
    this.scene.add(goal.group);
    this.net = goal.net;
    // The white goal line, the posts' width (it warms gold as the ball crosses).
    this.finishMat = new THREE.MeshBasicMaterial({ color: 0xeeeee8 });
    const line = new THREE.Mesh(
      new THREE.PlaneGeometry(CLIP_GOAL.postRadius * 2, ROAD_HALF_W * 2),
      this.finishMat,
    );
    line.rotation.x = -Math.PI / 2;
    line.position.set(FINISH_X, 0.02, 0);
    this.scene.add(line);

    // Guardrails: the game's course-wall language — a dark base with a thin
    // glowing CAP strip (a fully emissive wall reads as a flat blue slab).
    const railBaseMat = new THREE.MeshStandardMaterial({ color: 0x10141f, roughness: 0.72 });
    const railCapMat = new THREE.MeshStandardMaterial({
      color: 0x0a0d16,
      emissive: 0x2a76ff,
      emissiveIntensity: 1.7,
      roughness: 0.5,
    });
    for (const z of [-ROAD_HALF_W - 0.4, ROAD_HALF_W + 0.4]) {
      const rail = new THREE.Mesh(new THREE.BoxGeometry(260, 1.3, 0.5), railBaseMat);
      rail.position.set(0, 0.65, z);
      this.scene.add(rail);
      const cap = new THREE.Mesh(new THREE.BoxGeometry(260, 0.14, 0.56), railCapMat);
      cap.position.set(0, 1.36, z);
      this.scene.add(cap);
    }

    // A dark skyline card far behind so the horizon isn't empty.
    const skyline = new THREE.Mesh(
      new THREE.PlaneGeometry(400, 26),
      new THREE.MeshBasicMaterial({ color: 0x0b101d }),
    );
    skyline.position.set(0, 12, -60);
    this.scene.add(skyline);

    // Gold cross-line flash (billboard, revealed at the moment of crossing).
    this.flash = new THREE.Mesh(
      new THREE.PlaneGeometry(26, 15),
      new THREE.MeshBasicMaterial({
        color: 0xffe9a3,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    this.flash.position.set(FINISH_X, 4, LANE_Z);
    this.scene.add(this.flash);

    // Confetti pool (hidden until the crossing).
    const palette = [0xffd34a, 0xff5a4c, 0xe8edf5, 0x6ef0b9];
    for (let i = 0; i < 34; i++) {
      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(0.32, 0.22),
        new THREE.MeshBasicMaterial({
          color: palette[i % palette.length],
          side: THREE.DoubleSide,
        }),
      );
      mesh.visible = false;
      this.scene.add(mesh);
      this.confetti.push({
        mesh,
        vel: new THREE.Vector3(),
        spin: new THREE.Vector3(Math.random() * 9, Math.random() * 9, Math.random() * 9),
      });
    }

    // The two racer pairs: hero red (sports) + rival blue (muscle) — real
    // GLBs in real liveries, real BallViews. A load failure just means a
    // simple coloured body: the clip never blocks the guide.
    this.hero = this.buildPair(0xff3b3b, VEHICLES.sports.length / 2);
    this.rival = this.buildPair(0x3b82f6, VEHICLES.muscle.length / 2);
    this.carsReady = Promise.all([
      this.loadCarInto(this.hero.car, VEHICLES.sports, 0xff3b3b),
      this.loadCarInto(this.rival.car, VEHICLES.muscle, 0x3b82f6),
    ]);
  }

  private buildPair(color: number, reach: number): Pair {
    const car = new THREE.Group();
    this.scene.add(car);
    // A warm beauty light rides WITH each car — without it the paint reads
    // near-black against the night while the bright ball pops (measured).
    const light = new THREE.PointLight(0xffe6c4, 300, 36, 1.9);
    light.position.set(0, 5.5, 5);
    car.add(light);
    const ball = new BallView(color, BALL_R);
    this.scene.add(ball.mesh);
    return { car, ball, reach, free: null };
  }

  private loadCarInto(
    group: THREE.Group,
    def: (typeof VEHICLES)[string],
    color: number,
  ): Promise<void> {
    // NOTE: no wheel spin on purpose — the GLB wheels are baked at the scene
    // root (naive mesh rotation displaces them; spinifyWheels exists for
    // exactly this, M79c) and at this distance motion sells it.
    return modelLoader
      .load(def.url)
      .then((g) => {
        const fitted = ModelLoader.fit(g, def.length, def.forwardIsZ);
        applyCarMaterials(fitted, color, undefined, def.id);
        group.add(fitted);
      })
      .catch(() => {
        const body = new THREE.Mesh(
          new THREE.BoxGeometry(1.9, 0.9, 4),
          new THREE.MeshStandardMaterial({ color, roughness: 0.35, metalness: 0.5 }),
        );
        body.position.y = 0.75;
        group.add(body);
      });
  }

  private resetActors(): void {
    this.worldT = 0;
    this.wallT = 0;
    this.sinceCross = -1;
    this.timeScale = 1;
    this.shot = 'chase';
    this.fov = 44;
    this.camera.fov = 44;
    this.camera.updateProjectionMatrix();
    if (this.hero) this.hero.free = null;
    if (this.rival) this.rival.free = null;
    this.placePair(this.hero, START_X, LANE_Z, 0, 0, 0);
    this.placePair(this.rival, START_X - RIVAL_GAP, RIVAL_Z, 0, 0, 2.1);
    this.net?.reset();
    if (this.flash) (this.flash.material as THREE.MeshBasicMaterial).opacity = 0;
    if (this.finishMat) this.finishMat.color.set(0xeeeee8);
    for (const c of this.confetti) c.mesh.visible = false;
  }

  private resize(): void {
    if (!this.renderer || this.root.style.display === 'none') return;
    const w = Math.max(1, this.root.clientWidth);
    const h = Math.max(1, this.root.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  dispose(): void {
    this.finish();
    this.unsubViewport?.();
    this.env?.dispose();
    this.scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) for (const m of mat) m.dispose();
      else if (mat) mat.dispose();
    });
    this.renderer?.dispose();
    this.root.remove();
  }
}
